import { ConnectionInfo } from "./connection";
import { captureError, captureMessage } from "./utils/logger";
import { LCPData } from "./types";
import { fetchWithTimeout, FetchTimeoutError, API_FETCH_TIMEOUT_MS } from "../shared/fetchWithTimeout";
import { createHttpError } from "./utils/httpError";

type ApiPath = '/api/session-info' | '/api/metrics';

// Хост для отправки метрик мониторинга. Задаётся один раз явным вызовом initApi()
// из точки входа; на импорте ничего не исполняется (см. docs/KNOWN_ISSUES.md,
// правило про сайд-эффекты).
let monitoringHost = '';

export function initApi(host: string): void {
    monitoringHost = host;
}

function apiUrl(path: ApiPath): string {
    return monitoringHost + path;
}

// URL собственных запросов SDK — чтобы исключать их из resource-метрик.
export function getMonitoringUrls(): string[] {
    return [apiUrl('/api/session-info'), apiUrl('/api/metrics')];
}

export type SessionInfo = {
    siteId: string;
    sessionId: string;
    date: string;
    pageUrl: string;
    userAgent: string;
    connection: ConnectionInfo;
    // Version of the monitored page's own application.
    version: string;
    // Version of this SDK bundle, inlined at build time and sent unvalidated;
    // the server is what checks it. Absent when the build supplied nothing, and
    // absent in payloads from bundles cached on host pages from before this
    // field existed — the backend treats it as optional for exactly that reason.
    sdkVersion?: string;
}

async function apiRequest(method: string, path: ApiPath, data?: any) {
    try {
        const response = await fetchWithTimeout(apiUrl(path), {
            method,
            headers: {
                // text/plain keeps this a CORS "simple request": the browser sends
                // no preflight OPTIONS. The server parses the body as JSON anyway.
                'Content-Type': 'text/plain;charset=UTF-8',
            },
            body: data ? JSON.stringify(data) : undefined,
        }, API_FETCH_TIMEOUT_MS);

        if (!response.ok) {
            const errorText = await response.text();
            throw createHttpError(response.status, errorText);
        }

        // await, not bare return: a body-read rejection must hit this catch —
        // the fire-and-forget callers deliberately skip FetchTimeoutError.
        return await response.json();

    } catch (error) {
        if (error instanceof FetchTimeoutError) {
            captureMessage('API request timed out', { path, timeoutMs: API_FETCH_TIMEOUT_MS });
        } else {
            captureError(error, 'API request failed');
        }
        throw error;
    }
}


export const sendSessionInfo = (
    siteId: string,
    sessionId: string,
    version: string,
    sdkVersion: string | undefined,
    connection: ConnectionInfo) => {
    const data: SessionInfo = {
        siteId,
        sessionId,
        date: new Date().toISOString(),
        pageUrl: window.location.pathname,
        userAgent: navigator.userAgent,
        connection,
        version,
        // JSON.stringify drops the key when this is undefined.
        sdkVersion
    };

    //TODO: send connection info if changed during the session
    // Timeouts are already reported as the stable message inside apiRequest.
    apiRequest('POST', '/api/session-info', data).catch(error => {
        if (!(error instanceof FetchTimeoutError)) {
            captureError(error, 'Failed to send session info');
        }
    });
}

// Batch metrics sending function
export const sendBatchMetrics = (data: {
    LCP: LCPData[];
    LS: any[];
    INP: any[];
    navigation: PerformanceNavigationTiming[];
    FCP: any[];
    resource: PerformanceResourceTiming[];
    history: { url: string; start: number }[];
}, siteId: string, sessionId: string) => {

    const batchData = {
        ...data,
        siteId,
        sessionId
    };

    apiRequest('POST', '/api/metrics', batchData).catch(error => {
        if (!(error instanceof FetchTimeoutError)) {
            captureError(error, 'Failed to send batch metrics');
        }
    });
}
